import { useEffect, useState, useCallback } from 'react';
import {
  CreditCard, Eye, EyeOff, Plus, Wifi, Snowflake,
  Shield, CheckCircle2, Zap, Lock, Globe, AlertTriangle,
  RefreshCw, MoreHorizontal, Copy, Check,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { Card as CardType, maskCardNumber } from '@/lib/index';
import { useToast } from '@/hooks/use-toast';

const CARD_GRADIENTS: Record<string, string> = {
  dark: 'linear-gradient(135deg, #0d1b3e 0%, #1a2a5e 50%, #0d1b3e 100%)',
  gold: 'linear-gradient(135deg, #b8941f 0%, #D4AF37 40%, #c9a227 100%)',
  metal: 'linear-gradient(135deg, #374151 0%, #4b5563 50%, #374151 100%)',
  blue: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #1e40af 100%)',
  white: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
};

interface ExtendedCard extends CardType {
  color?: string;
  card_number_masked?: string;
  contactless_enabled?: boolean;
  online_payments_enabled?: boolean;
  atm_enabled?: boolean;
  daily_limit?: number;
}

function PremiumCard({
  card,
  selected,
  onSelect,
  onToggle,
}: {
  card: ExtendedCard;
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
}) {
  const [showFull, setShowFull] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useI18n();
  const isFrozen = card.status === 'frozen';
  const cardColor = card.color || 'dark';
  const isGold = cardColor === 'gold';
  const textColor = cardColor === 'white' ? '#0f172a' : '#ffffff';

  const displayNumber = showFull
    ? (card.card_number_masked || card.card_number || '').replace(/\*/g, '4')
    : maskCardNumber(card.card_number_masked || card.card_number || '');

  const copyNumber = () => {
    navigator.clipboard.writeText(card.card_number_masked || card.card_number || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ scale: selected ? 1 : 1.01 }}
      onClick={onSelect}
      className={`cursor-pointer transition-all ${selected ? 'ring-2 ring-gold rounded-3xl' : ''}`}
    >
      <div
        className={`relative w-full rounded-3xl p-6 overflow-hidden select-none transition-all ${isFrozen ? 'opacity-60 grayscale' : ''}`}
        style={{
          background: CARD_GRADIENTS[cardColor] ?? CARD_GRADIENTS.dark,
          aspectRatio: '1.586',
          boxShadow: selected
            ? '0 30px 80px -10px rgba(212,175,55,0.3), 0 0 0 2px rgba(212,175,55,0.4)'
            : '0 20px 60px -10px rgba(0,0,0,0.4)',
        }}
      >
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(ellipse at 30% 20%, white 0%, transparent 60%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, white 0px, white 1px, transparent 1px, transparent 8px)' }} />

        <div className="flex items-start justify-between relative z-10">
          <div>
            <p className="font-bold text-lg tracking-widest" style={{ color: isGold ? '#0f172a' : 'rgba(255,255,255,0.9)' }}>CYF24</p>
            <p className="text-[10px] uppercase tracking-[0.3em] mt-0.5" style={{ color: isGold ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)' }}>BANK</p>
          </div>
          <div className="flex items-center gap-2">
            {isFrozen && <Snowflake className="w-5 h-5 text-blue-300" />}
            <Wifi className="w-6 h-6 rotate-90" style={{ color: isGold ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.6)' }} />
          </div>
        </div>

        <div className="mt-4 mb-3 relative z-10">
          <div className="w-10 h-7 rounded-md" style={{ background: isGold ? 'rgba(0,0,0,0.25)' : 'rgba(212,175,55,0.6)', border: isGold ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(212,175,55,0.4)' }}>
            <div className="grid grid-cols-2 h-full gap-px p-1 opacity-60">
              <div className="rounded-sm" style={{ background: isGold ? 'rgba(0,0,0,0.3)' : 'rgba(212,175,55,0.8)' }} />
              <div className="rounded-sm" style={{ background: isGold ? 'rgba(0,0,0,0.3)' : 'rgba(212,175,55,0.8)' }} />
              <div className="rounded-sm col-span-2" style={{ background: isGold ? 'rgba(0,0,0,0.2)' : 'rgba(212,175,55,0.6)' }} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 relative z-10">
          <p className="font-mono text-base tracking-[0.15em]" style={{ color: textColor, opacity: 0.9 }}>
            {displayNumber}
          </p>
          <button onClick={(e) => { e.stopPropagation(); setShowFull(p => !p); }} className="opacity-50 hover:opacity-90 transition-opacity">
            {showFull ? <EyeOff className="w-3.5 h-3.5" style={{ color: textColor }} /> : <Eye className="w-3.5 h-3.5" style={{ color: textColor }} />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); copyNumber(); }} className="opacity-50 hover:opacity-90 transition-opacity">
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" style={{ color: textColor }} />}
          </button>
        </div>

        <div className="flex items-end justify-between relative z-10">
          <div>
            <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: isGold ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)' }}>Posiadacz</p>
            <p className="text-sm font-semibold tracking-wide uppercase" style={{ color: textColor }}>{card.card_holder_name}</p>
          </div>
          <div className="text-center">
            <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: isGold ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)' }}>Ważna do</p>
            <p className="text-sm font-mono" style={{ color: textColor }}>
              {String(card.expiry_month).padStart(2, '0')}/{String(card.expiry_year).slice(-2)}
            </p>
          </div>
          <div>
            <div className="flex">
              <div className="w-7 h-7 rounded-full" style={{ background: isGold ? 'rgba(0,0,0,0.35)' : 'rgba(239,68,68,0.8)' }} />
              <div className="w-7 h-7 rounded-full -ml-3" style={{ background: isGold ? 'rgba(0,0,0,0.2)' : 'rgba(234,179,8,0.8)' }} />
            </div>
            <p className="text-[9px] uppercase text-right mt-1" style={{ color: isGold ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)' }}>{card.card_network}</p>
          </div>
        </div>

        {isFrozen && (
          <div className="absolute inset-0 flex items-center justify-center rounded-3xl z-20">
            <div className="bg-blue-900/60 backdrop-blur-sm border border-blue-400/30 rounded-2xl px-5 py-2.5 flex items-center gap-2">
              <Snowflake className="w-4 h-4 text-blue-300" />
              <span className="text-blue-200 text-sm font-semibold">Zamrożona</span>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mt-3">
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Badge
                    className="text-xs capitalize"
                    style={
                      card.status === 'active'
                        ? { background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }
                        : card.status === 'frozen'
                        ? { background: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)' }
                        : { background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }
                    }
                  >
                    {card.status === 'active' ? '● Aktywna' : card.status === 'frozen' ? '❄ Zamrożona' : card.status === 'blocked' ? 'Zablokowana' : card.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground capitalize">
                    {card.card_type === 'debit' ? 'Debetowa' : card.card_type === 'credit' ? 'Kredytowa' : 'Wirtualna'} · {card.card_network}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-8 h-8">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onToggle}>
                      {isFrozen ? <><RefreshCw className="w-3.5 h-3.5 mr-2" />Odblokuj kartę</> : <><Snowflake className="w-3.5 h-3.5 mr-2" />Zamroź kartę</>}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive">
                      <AlertTriangle className="w-3.5 h-3.5 mr-2" />Zgłoś zgubienie
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-border/30">
                {[
                  { label: 'Limit dzienny', value: `£${card.daily_limit?.toLocaleString() ?? '1,000'}` },
                  { label: 'Typ', value: card.card_type === 'debit' ? 'Debetowa' : card.card_type === 'credit' ? 'Kredytowa' : 'Wirtualna' },
                  { label: 'Sieć', value: card.card_network },
                ].map((s) => (
                  <div key={s.label} className="text-center p-2 rounded-xl bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                    <p className="text-sm font-semibold text-foreground">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'contactless_enabled', label: 'Zbliżeniowe', icon: Wifi, checked: card.contactless_enabled ?? true },
                  { key: 'online_payments_enabled', label: 'Płatności online', icon: Globe, checked: card.online_payments_enabled ?? true },
                  { key: 'atm_enabled', label: 'Wypłaty z bankomatów', icon: Lock, checked: card.atm_enabled ?? true },
                ].map((feat) => {
                  const Icon = feat.icon;
                  return (
                    <div key={feat.key} className="flex flex-col items-center gap-2 p-2.5 rounded-xl border border-border/30">
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <Switch checked={!!feat.checked} onCheckedChange={() => {}} disabled />
                      <Label className="text-[10px] text-muted-foreground text-center leading-tight">{feat.label}</Label>
                    </div>
                  );
                })}
              </div>

              <Button
                variant={isFrozen ? 'default' : 'outline'}
                size="sm"
                className="w-full mt-4 gap-2"
                style={isFrozen ? { background: 'rgba(74,222,128,0.15)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' } : { borderColor: 'rgba(96,165,250,0.3)', color: '#60a5fa' }}
                onClick={onToggle}
              >
                {isFrozen ? <><RefreshCw className="w-4 h-4" />Odblokuj kartę</> : <><Snowflake className="w-4 h-4" />Zamroź kartę</>}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function DashboardCards() {
  const { account, profile } = useAuth();
  const { t } = useI18n();
  const [cards, setCards] = useState<ExtendedCard[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const { toast } = useToast();

  const fetchCards = useCallback(async () => {
    if (!profile?.id) return;
    const { data } = await supabase.from('cards').select('*').eq('user_id', profile.id).order('created_at', { ascending: false });
    const list = (data ?? []) as ExtendedCard[];
    setCards(list);
    if (list.length > 0 && !selectedId) setSelectedId(list[0].id);
    setLoading(false);
  }, [profile, selectedId]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const toggleStatus = async (cardId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'frozen' ? 'active' : 'frozen';
    const { error } = await supabase.from('cards').update({ status: newStatus }).eq('id', cardId);
    if (!error) {
      setCards(prev => prev.map(c => c.id === cardId ? { ...c, status: newStatus as CardType['status'] } : c));
      toast({ title: newStatus === 'frozen' ? 'Karta zamrożona' : 'Karta aktywna', description: newStatus === 'frozen' ? 'Twoja karta została tymczasowo zamrożona' : 'Twoja karta została odblokowana' });
    } else {
      toast({ title: 'Błąd', description: 'Nie udało się zmienić statusu karty', variant: 'destructive' });
    }
  };

  const orderCard = async (type: 'debit' | 'credit' = 'debit', isVirtual = false) => {
    if (!account || !profile) return;
    setOrdering(true);
    const last4 = String(Math.floor(Math.random() * 9999)).padStart(4, '0');
    const masked = `**** **** **** ${last4}`;
    const { data, error } = await supabase.from('cards').insert({
      user_id: profile.id,
      account_id: account.id,
      card_number: masked,
      card_number_masked: masked,
      card_holder_name: `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim().toUpperCase(),
      expiry_month: new Date().getMonth() + 1,
      expiry_year: new Date().getFullYear() + 3,
      card_type: type,
      card_network: 'Visa',
      color: isVirtual ? 'blue' : 'dark',
      status: 'active',
      is_virtual: isVirtual,
      contactless_enabled: true,
      online_payments_enabled: true,
      atm_enabled: true,
      daily_limit: 1000,
    }).select().single();
    if (!error && data) {
      setCards(prev => [data as ExtendedCard, ...prev]);
      setSelectedId((data as ExtendedCard).id);
      toast({ title: 'Karta zamówiona', description: isVirtual ? 'Twoja wirtualna karta jest gotowa do użycia' : 'Twoja fizyczna karta zostanie dostarczona w ciągu 5-7 dni roboczych' });
    } else {
      toast({ title: 'Błąd', description: 'Nie udało się zamówić karty', variant: 'destructive' });
    }
    setOrdering(false);
  };

  const selectedCard = cards.find(c => c.id === selectedId);

  return (
    <DashboardLayout title="Karty">
      <div className="max-w-5xl mx-auto space-y-6">

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Twoje karty</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Zarządzaj swoimi kartami debetowymi i kredytowymi</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5 border-border/50" onClick={() => orderCard('debit', true)} disabled={ordering}>
              <Plus className="w-3.5 h-3.5" />Wirtualna karta
            </Button>
            <Button size="sm" className="gap-1.5 bg-gold text-background hover:bg-gold/90" onClick={() => orderCard('debit', false)} disabled={ordering}>
              <CreditCard className="w-3.5 h-3.5" />{ordering ? 'Zamawianie...' : 'Zamów kartę fizyczną'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2">
            {[1, 2].map(i => (
              <div key={i} className="space-y-3 animate-pulse">
                <div className="aspect-[1.586] bg-muted rounded-3xl" />
                <div className="h-44 bg-muted rounded-2xl" />
              </div>
            ))}
          </div>
        ) : cards.length === 0 ? (
          <Card className="border-border/40 border-dashed">
            <CardContent className="py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Nie masz jeszcze żadnych kart</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">Zamów swoją pierwszą kartę, aby rozpocząć korzystanie z CYF24 Bank</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" className="gap-2" onClick={() => orderCard('debit', true)} disabled={ordering}>
                  <Plus className="w-4 h-4" />Wirtualna karta
                </Button>
                <Button className="gap-2 bg-gold text-background hover:bg-gold/90" onClick={() => orderCard('debit', false)} disabled={ordering}>
                  <CreditCard className="w-4 h-4" />Karta fizyczna
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-8 md:grid-cols-2">
            <AnimatePresence>
              {cards.map(card => (
                <PremiumCard
                  key={card.id}
                  card={card}
                  selected={card.id === selectedId}
                  onSelect={() => setSelectedId(card.id === selectedId ? null : card.id)}
                  onToggle={() => toggleStatus(card.id, card.status)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        <Card className="border-border/40">
          <CardHeader className="pb-3 border-b border-border/30">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="w-4 h-4 text-gold" />
              Bezpieczeństwo kart
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 grid sm:grid-cols-3 gap-4">
            {[
              { icon: Lock, title: 'Weryfikacja 3D Secure', desc: 'Dodatkowa warstwa ochrony dla płatności online', color: '#60a5fa' },
              { icon: Snowflake, title: 'Natychmiastowe zamrażanie', desc: 'Zablokuj kartę w aplikacji w dowolnym momencie', color: '#67e8f9' },
              { icon: Zap, title: 'Limity transakcji', desc: 'Ustaw własne limity dzienne i miesięczne', color: '#D4AF37' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex gap-3 p-3 rounded-xl bg-muted/20 border border-border/20">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${item.color}18`, border: `1px solid ${item.color}30` }}>
                    <Icon className="w-4 h-4" style={{ color: item.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {selectedCard && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-border/40">
              <CardHeader className="pb-3 border-b border-border/30">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  Szczegóły karty · {selectedCard.card_holder_name}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Posiadacz', value: selectedCard.card_holder_name },
                    { label: 'Ważna do', value: `${String(selectedCard.expiry_month).padStart(2, '0')}/${String(selectedCard.expiry_year).slice(-2)}` },
                    { label: 'Sieć', value: selectedCard.card_network },
                    { label: 'Limit dzienny', value: `£${selectedCard.daily_limit?.toLocaleString() ?? '1,000'}` },
                  ].map(row => (
                    <div key={row.label} className="p-3 rounded-xl bg-muted/20">
                      <p className="text-xs text-muted-foreground mb-1">{row.label}</p>
                      <p className="text-sm font-semibold text-foreground">{row.value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}